import { DOCUMENT } from '@angular/common';
import { computed, DestroyRef, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';

const STORAGE_KEY = 'idoc-pwa-install-prompt';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type InstallInstructions = 'ios-safari' | 'ios-other' | 'mac-safari' | 'firefox' | 'browser';

@Injectable({ providedIn: 'root' })
export class PwaInstallPromptService {
  private readonly document: Document = inject(DOCUMENT);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly browser: Window | null = this.document.defaultView;
  private readonly deferredPrompt: WritableSignal<BeforeInstallPromptEvent | null> = signal(null);
  private readonly hidden: WritableSignal<boolean> = signal(false);
  private readonly prompting: WritableSignal<boolean> = signal(false);
  private initialized: boolean = false;
  private installed: boolean = false;

  readonly visible: Signal<boolean> = computed(() => !this.hidden());
  readonly canPrompt: Signal<boolean> = computed(() => this.deferredPrompt() !== null && !this.prompting());
  readonly instruction: InstallInstructions;

  constructor() {
    this.instruction = this.detectInstructions(this.browser?.navigator);
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    const browser = this.browser;
    if (!browser) {
      this.hidden.set(true);
      return;
    }

    this.hidden.set(this.hasSavedDecision() || this.isStandalone());

    const onBeforeInstallPrompt = (event: Event): void => {
      if (!this.hidden()) {
        event.preventDefault();
        this.deferredPrompt.set(event as BeforeInstallPromptEvent);
      }
    };
    const onInstalled = (): void => {
      this.installed = true;
      this.saveDecision('installed');
    };
    const onDisplayModeChange = (): void => {
      if (this.isStandalone()) {
        this.hidden.set(true);
        this.deferredPrompt.set(null);
      }
    };

    browser.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    browser.addEventListener('appinstalled', onInstalled);
    const displayMode = browser.matchMedia?.('(display-mode: standalone)');
    displayMode?.addEventListener?.('change', onDisplayModeChange);

    this.destroyRef.onDestroy(() => {
      browser.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      browser.removeEventListener('appinstalled', onInstalled);
      displayMode?.removeEventListener?.('change', onDisplayModeChange);
    });
  }

  dismiss(): void {
    this.saveDecision('dismissed');
  }

  async install(): Promise<void> {
    const prompt = this.deferredPrompt();
    if (!prompt || this.prompting()) {
      return;
    }

    this.deferredPrompt.set(null);
    this.prompting.set(true);
    try {
      // prompt() вызывается по клику до первого await.
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (!this.installed) {
        if (choice.outcome === 'dismissed') {
          this.saveDecision('dismissed');
        } else {
          // Принятие диалога не гарантирует завершённую установку; её подтвердит appinstalled.
          this.hidden.set(true);
        }
      }
    } catch {
      // Браузер может отозвать сохранённый запрос; ручная инструкция остаётся видимой.
    } finally {
      this.prompting.set(false);
    }
  }

  private hasSavedDecision(): boolean {
    try {
      const value = this.browser?.localStorage.getItem(STORAGE_KEY);
      return value === 'dismissed' || value === 'installed';
    } catch {
      return false;
    }
  }

  private saveDecision(value: 'dismissed' | 'installed'): void {
    this.hidden.set(true);
    this.deferredPrompt.set(null);
    try {
      this.browser?.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // В приватном режиме хранилище может быть недоступно; в текущей сессии баннер всё равно скрыт.
    }
  }

  private isStandalone(): boolean {
    const navigator = this.browser?.navigator as (Navigator & { standalone?: boolean }) | undefined;
    return navigator?.standalone === true || this.browser?.matchMedia?.('(display-mode: standalone)').matches === true;
  }

  private detectInstructions(navigator: Navigator | undefined): InstallInstructions {
    const userAgent = navigator?.userAgent ?? '';
    const isIos =
      /iPhone|iPad|iPod/i.test(userAgent) ||
      (navigator?.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1);
    const isFirefox = /Firefox|FxiOS/i.test(userAgent);
    const isSafari = /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS|Edg|OPR|FxiOS/i.test(userAgent);

    if (isFirefox) {
      return 'firefox';
    }
    if (isIos) {
      return isSafari ? 'ios-safari' : 'ios-other';
    }
    return isSafari ? 'mac-safari' : 'browser';
  }
}
